import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { setupI18n } from "@/lib/i18n/setup";
import Header from "@/components/layout/header";
import Footer from "@/components/layout/footer";
import { supportedLanguages } from "@/lib/i18n/init";
import {
  getPreferredLocale,
  LANGUAGE_RESPONSE_CACHE_HEADERS,
} from "@/lib/i18n/actions";
import { websiteConfigQueryOptions } from "@/lib/website-config";
import { parseHeadScript } from "@/lib/head-script";
import { useSuspenseQuery } from "@tanstack/react-query";
import GeneralError from "@/components/general-error";
import { QrTableBanner } from "@/components/qr-table-banner";
import { openingHoursQueryOptions } from "@/lib/shifts";
import Subscriptions from "@/components/subscriptions";
import appCss from "../styles.css?url";
import { checkCoupon, couponsQueryOptions } from "@/lib/coupons";
import { useEffect } from "react";
import { useCartStore } from "@/components/cart/store";
import { useShallow } from "zustand/react/shallow";
import { useIsQrSession } from "@/stores/qr-session";
import { CouponContext } from "@/lib/supabase/enums";
import { StorefrontHeader } from "@/components/storefront/storefront-header";
import { SalesChannels } from "@/lib/storefront/channel";
import { PAGE_SCROLL_CONTAINER_ID } from "@/lib/page-scroll-container";

export const Route = createFileRoute("/$locale")({
  errorComponent: GeneralError,
  beforeLoad: async ({ params, location }) => {
    const locale = params.locale;
    const preferredLocale = await getPreferredLocale();

    const removeLocaleFromPath = (pathname: string) => {
      const pathSegments = pathname.split("/").filter(Boolean);

      const commonLocales = [
        "en",
        "es",
        "fr",
        "de",
        "it",
        "pt",
        "ru",
        "ja",
        "ko",
        "zh",
        "ar",
        "hi",
        "nl",
        "sv",
        "da",
        "no",
        "fi",
        "pl",
        "tr",
        "he",
        "th",
        "vi",
        "id",
        "ms",
      ];

      // Our own top-level routes must never be mistaken for a language, or a
      // locale-less link to one gets its first segment eaten and lands on a
      // Not Found. "qr" is the reason this list exists: a printed table sticker
      // points at /qr/t/<token> and is two lowercase letters just like a
      // language code.
      const appSegments = ["qr", "menu", "order", "checkout"];

      let pathWithoutLocale = pathname;
      if (pathSegments.length > 0) {
        const firstSegment = pathSegments[0];
        const isLikelyLocale =
          !appSegments.includes(firstSegment) &&
          (commonLocales.includes(firstSegment) ||
            (firstSegment.length === 2 && /^[a-z]{2}$/.test(firstSegment)));

        if (isLikelyLocale) {
          pathSegments.shift();
          pathWithoutLocale =
            pathSegments.length > 0 ? `/${pathSegments.join("/")}` : "/";
        }
      }

      return pathWithoutLocale;
    };

    if (!supportedLanguages.includes(locale)) {
      const pathWithoutLocale = removeLocaleFromPath(location.pathname);

      const redirectPath = `/${preferredLocale}${pathWithoutLocale === "/" ? "" : pathWithoutLocale
        }`;

      throw redirect({
        to: redirectPath as any,
        search: location.search as any,
        replace: true,
        // Locale chosen from Accept-Language / cookie — keep it out of shared caches.
        headers: LANGUAGE_RESPONSE_CACHE_HEADERS,
      });
    }

    if (
      supportedLanguages.includes(preferredLocale) &&
      preferredLocale !== locale
    ) {
      const pathWithoutLocale = removeLocaleFromPath(location.pathname);

      const redirectPath = `/${preferredLocale}${pathWithoutLocale === "/" ? "" : pathWithoutLocale
        }`;

      throw redirect({
        to: redirectPath as any,
        search: location.search as any,
        replace: true,
        // Locale chosen from Accept-Language / cookie — keep it out of shared caches.
        headers: LANGUAGE_RESPONSE_CACHE_HEADERS,
      });
    }

    await setupI18n(locale);

    return { locale };
  },
  loader: async ({ context }) => {
    const websiteConfig = await context.queryClient.ensureQueryData(
      websiteConfigQueryOptions()
    );

    return {
      openingHours: context.queryClient.ensureQueryData(
        openingHoursQueryOptions()
      ),
      websiteConfig,

      coupons: context.queryClient.ensureQueryData(couponsQueryOptions()),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: loaderData?.websiteConfig.metaTitle
          ? loaderData.websiteConfig.metaTitle
          : loaderData?.websiteConfig.companyName,
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      // Absolute, so clients that guess the icon relative to the page don't
      // request /nl/order/favicon.ico and land on the order route.
      {
        rel: "icon",
        href: "/favicon.ico",
      },
    ],
    scripts: loaderData?.websiteConfig.headScript
      ? parseHeadScript(loaderData.websiteConfig.headScript)
      : undefined,
  }),

  component: RouteComponent,
});

function RouteComponent() {
  //global state
  const { setCoupon, appliedCouponCode, bindToMerchant } = useCartStore(
    useShallow((state) => ({
      setCoupon: state.setCoupon,
      appliedCouponCode: state.cart.coupon?.code,
      bindToMerchant: state.bindToMerchant,
    }))
  );
  const isQrSession = useIsQrSession();

  // A guest ordering from a table is placing an in_store order, not an online
  // one — the app it happens to run in makes no difference to the bill. Coupons
  // are therefore judged against that context, so one the merchant limited to
  // delivery/pickup stays off the table's order.
  //queries
  const websiteConfig = useSuspenseQuery(websiteConfigQueryOptions());
  const isStorefront = websiteConfig.data.channel === SalesChannels.storefront;

  // The webshop's coupons are judged as webshop coupons for the same reason.
  const couponContext = isQrSession
    ? CouponContext.in_store
    : isStorefront
      ? CouponContext.webshop
      : undefined;
  const couponsData = useSuspenseQuery(couponsQueryOptions());

  // Merchants without a domain share the stable QR host and its saved cart.
  useEffect(() => {
    bindToMerchant(websiteConfig.data.id);
  }, [websiteConfig.data.id, bindToMerchant]);

  useEffect(() => {
    // An auto-apply coupon is resolved by its code, so one saved without a code
    // has nothing to look up.
    if (couponsData.data?.code) {
      checkCoupon({
        data: { code: couponsData.data.code, context: couponContext },
      })
        .then((result) => {
          if (result.success && result.coupon) {
            setCoupon({
              code: result.coupon.code,
              coupon: result.coupon,
            });
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [couponsData.data, couponContext, setCoupon]);

  // Scanning a table narrows the context, so a coupon that was already on the
  // cart — auto-applied or typed in while the guest was browsing the webshop —
  // has to be re-judged and dropped if it does not hold at a table.
  useEffect(() => {
    if (!couponContext || !appliedCouponCode) return;

    checkCoupon({ data: { code: appliedCouponCode, context: couponContext } })
      .then((result) => {
        if (!result.success) setCoupon(undefined);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [couponContext, appliedCouponCode, setCoupon]);

  return (
    <>
      <main
        id={PAGE_SCROLL_CONTAINER_ID}
        className="flex-1 overflow-auto flex flex-col"
      >
        {isStorefront ? (
          <StorefrontHeader
            logoUrl={websiteConfig.data.logo}
            companyName={websiteConfig.data.companyName}
          />
        ) : (
          <>
            <Header
              logoUrl={websiteConfig.data.logo}
              companyName={websiteConfig.data.companyName}
            />
            <QrTableBanner />
          </>
        )}
        <Subscriptions origin={websiteConfig.data.origin} />

        <div className="flex flex-1 flex-col">
          <Outlet />
        </div>
        <Footer />
        <div className="border-t flex items-center justify-center p-2">Powered by <a className="font-semibold" href="https://www.vrodex.com" target="_blank" rel="noopener noreferrer">&nbsp;Vrodex</a></div>
      </main>
    </>
  );
}
